import React from 'react';

export default function Combobox(props: { values: string[], onChange?: (value: string) => void } & Omit<Partial<React.HTMLProps<HTMLDivElement>>, 'onChange'>) {
	const [value, setValue] = React.useState('');
	const [ghost, setGhost] = React.useState<string | null>(null);
	const [index, setIndex] = React.useState(0);
	const skip = React.useRef(false);

	const debounce = React.useMemo(() => value, [value]);
	React.useEffect(() => props.onChange?.(debounce), [debounce]);

	const fuzzy = (value: string) => value.replaceAll(/\W/g, '').toLowerCase();

	const fuzzyInputs = React.useMemo(() => props.values.map(i => [i, fuzzy(i)] as const), [props.values]);
	const fuzzyValue = React.useMemo(() => fuzzy(value), [value]);

	const remaining = React.useMemo(() => fuzzyInputs
		.filter(([, fuzzy]) => fuzzy.startsWith(fuzzyValue))
		.map(([value]) => value), [fuzzyInputs, fuzzyValue]);

	const matches = React.useMemo(() => props.values
		.filter(v => v.toLowerCase().startsWith(value.toLowerCase())), [props.values, value]);

	const ref = React.useRef<HTMLInputElement>(null);
	const popover = React.useRef<HTMLUListElement>(null);

	React.useLayoutEffect(() => {
		if (ghost) ref.current?.setSelectionRange(value.length, ghost.length);
	}, [ghost, value]);

	const accept = () => {
		if (!ghost) return;
		setValue(ghost);
		setGhost(null);
		const len = ghost.length;
		requestAnimationFrame(() => ref.current?.setSelectionRange(len, len));
	};

	const cycle = (dir: 1 | -1) => {
		if (!matches.length) return;
		const next = (index + dir + matches.length) % matches.length;
		setIndex(next);
		setGhost(matches[next]);
	};

	const onFocus = () => {
		ref.current?.focus();

		if (remaining.length > 1)
			popover.current?.showPopover();
		else
			popover.current?.hidePopover();
	};

	const onBlur = () => {
		popover.current?.hidePopover();
	}

	const divProps: Omit<typeof props, 'values' | 'onChange'> = React.useMemo(() =>
			Object.assign({}, props, {values: undefined, onChange: undefined}),
		[props]);

	return <div className='combobox' tabIndex={0} {...divProps} onFocus={onFocus} onBlur={onBlur}>
		<input
			ref={ref}
			type='text'
			value={ghost ? value + ghost.slice(value.length) : value}
			onChange={e => {
				const raw = e.currentTarget.value;
				setValue(raw);
				setIndex(0);
				setGhost(skip.current ? null : props.values.find(v => v.toLowerCase().startsWith(raw.toLowerCase())) ?? null);
				skip.current = false;
			}}
			onKeyDown={e => {
				const hasGhost = !!ghost && ref.current?.selectionStart !== ref.current?.selectionEnd;
				if (e.key === 'Backspace' || e.key === 'Delete') skip.current = true;
				else if ((e.key === 'Tab' || e.key === 'Enter') && hasGhost) { e.preventDefault(); accept(); }
				else if (e.key === 'Escape') setGhost(null);
				else if (e.key === 'ArrowDown') { e.preventDefault(); cycle(1); }
				else if (e.key === 'ArrowUp') { e.preventDefault(); cycle(-1); }
			}}
			tabIndex={-1} />
		<button
			className='symbolic'
			type='button'
			data-icon={'\ue5c5'}
			tabIndex={-1}
			onMouseDown={e => {
				e.preventDefault();
				if (remaining.length > 1)
					popover.current?.showPopover();
			}} />

		<ul ref={popover} className='suggestions' popover='manual'>
			{remaining.map((i, a) => <li key={`combobox-suggestion-${a}-${i}`}
				onClick={() => setValue(i)}>{i}</li>)}
		</ul>
	</div>;
}